import { ReactElement, useEffect } from 'react'
import {
  EditableDropdown,
  FlexWrapper,
  Input,
  PageWrapper,
  DropdownWrapper,
  ActionButton,
  SectionTitle,
  Button,
  Loader,
  TostMessage
} from '../../../../components'
import strings from '../../../../locale/en'
import DatePicker from 'react-datepicker'
import { useHistory } from 'react-router'
import { status as statusList } from '../../../../const/dropdown'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../../redux/store'
import { AddBatchForm } from '../../../../redux/batch/types'
import {
  updateFormValues,
  updateFormErrors,
  saveBatch,
  resetValues,
  resetError
} from '../../../../redux/batch/action'
import { validateRequired } from '../../../../helpers/formValidation'
import {
  getInstitutes,
  getAvailableCourses,
  resetAvailableCourse
} from '../../../../redux/coachingCenter/actions'
import {
  getBranchDropdown,
  getCoursesDropdown,
  getInstituteDropdown
} from '../../../../helpers'
import { getBranchesByInstitute } from '../../../../redux/branch/action'
import {
  getChildByInstitute,
  resetCourses
} from '../../../../redux/course/action'
import ROUTES from '../../../../const/routes'
import { ROLE } from '../../../../helpers/determineMenu'

const AddBatch = (): ReactElement => {
  const {
    formValues,
    formErrors,
    institutes,
    branches,
    courses,
    availableCourses,
    isLoading,
    error,
    userDetail,
    userRole
  } = useSelector(
    (state: RootState) => ({
      formValues: state.batch.formValues,
      formErrors: state.batch.formErrors,
      institutes: state.coachingCenter.institutes,
      branches: state.branch.branches,
      courses: state.course.courses,
      availableCourses: state.coachingCenter.availableCourses,
      isLoading: state.batch.isLoading,
      error: state.batch.error,
      userDetail: state.user?.userInfo?.userDetail,
      userRole: state.user.userInfo?.role
    }),
    shallowEqual
  )
  const history = useHistory()
  const dispatch = useDispatch()
  const isEditMode = history.location.pathname !== ROUTES.BATCH_ADD
  const defaultInstituteId = userDetail?.coachingCenterId || ''

  const {
    batchName: batchNameValue = '',
    coachingCenterId,
    coachingCenterBranchId,
    batchSize: batchSizeValue,
    description: descriptionValue,
    endTiming: endTimingValue,
    startTiming: startTimingValue,
    batchStartDate: batchStartDateValue,
    batchEndDate: batchEndDateValue
  } = formValues || ({} as AddBatchForm)
  const {
    batchName: batchNameError = '',
    coachingCenterId: instituteError = '',
    coachingCenterBranchId: branchError = '',
    courseId: courseError = '',
    status: statusError = '',
    description: descriptionError = '',
    batchSize: batchSizeError = ''
  } = formErrors || ({} as AddBatchForm)

  const {
    batchDetails: {
      batchName,
      instituteName,
      branchName,
      courseName,
      upmyranksMaterial,
      batchStartDate,
      batchEndDate,
      startTiming,
      endTiming,
      batchSize,
      description,
      status,
      placeholders: {
        selectInstitute,
        selectBranchName,
        selectCourseName,
        selectUpmyranksMaterial,
        selectAnOption
      }
    },
    menu: {
      batch: { add, editBatch }
    },
    button: { save, back }
  } = strings

  const instituteList = institutes ? getInstituteDropdown(institutes) : []
  const branchList = branches ? getBranchDropdown(branches) : []
  const courseList = courses ? getCoursesDropdown(courses) : []
  const availableCoursesList = availableCourses
    ? getCoursesDropdown(availableCourses)
    : []

  const handleValidate = (key: string) => {
    const error = validateRequired(batchNameValue)
    dispatch(
      updateFormErrors({
        key,
        value: error
      })
    )
  }

  const handleSubmit = () => {
    dispatch(
      saveBatch({
        batchId: formValues?.id,
        isEdit: isEditMode
      })
    )
  }

  useEffect(() => {
    if (userRole === ROLE.INSTITUTE_ADMIN) {
      dispatch(getBranchesByInstitute(defaultInstituteId))
      dispatch(getAvailableCourses(defaultInstituteId))
      dispatch(
        updateFormValues({
          key: 'coachingCenterId',
          value: defaultInstituteId
        })
      )
    } else {
      dispatch(getInstitutes())
    }

    return () => {
      dispatch(resetValues())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isEditMode) {
      dispatch(getBranchesByInstitute(coachingCenterId))
      dispatch(getAvailableCourses(coachingCenterId))
      dispatch(
        getChildByInstitute({
          instituteId: coachingCenterId,
          branchId: coachingCenterBranchId
        })
      )
    } else {
      dispatch(resetValues())
      dispatch(resetError())
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode])

  return (
    <PageWrapper id="container">
      <SectionTitle title={isEditMode ? editBatch : add} hasBorder />
      <TostMessage
        show={!!error}
        onCloseHandler={() => dispatch(resetError())}
        message={error}
      />
      <FlexWrapper justifyContent="space-around">
        <DropdownWrapper>
          <Input
            label={batchName}
            isRequired
            isDisabled={isEditMode}
            placeholder={batchName}
            value={batchNameValue}
            name="batchName"
            error={batchNameError}
            onBlur={() => {
              handleValidate('batchName')
            }}
            onChange={(value: string) => {
              dispatch(
                updateFormValues({
                  key: 'batchName',
                  value: value
                })
              )
            }}
          />
        </DropdownWrapper>
        {userRole !== ROLE.INSTITUTE_ADMIN && (
          <DropdownWrapper>
            <EditableDropdown
              title={instituteName}
              isRequired
              dropdownList={instituteList}
              placeholder={selectInstitute}
              error={instituteError}
              defaultValue={
                isEditMode
                  ? {
                    name: formValues?.coachingCenterId
                      ? instituteList?.filter(
                        (institute) =>
                          institute?.id === formValues?.coachingCenterId
                      )[0]?.name
                      : '',
                    id: formValues.coachingCenterId
                  }
                  : null
              }
              handleSelect={(item) => {
                dispatch(getBranchesByInstitute(item?.id))
                dispatch(getAvailableCourses(item?.id))
                dispatch(
                  updateFormValues({
                    key: 'coachingCenterId',
                    value: item?.id
                  })
                )
                dispatch(resetCourses())
                dispatch(resetAvailableCourse())
              }}
            />
          </DropdownWrapper>
        )}
        <DropdownWrapper>
          <EditableDropdown
            title={branchName}
            isRequired
            dropdownList={branchList}
            placeholder={selectBranchName}
            error={branchError}
            defaultValue={
              isEditMode
                ? {
                  name: formValues?.coachingCenterBranchId
                    ? branchList?.filter(
                      (branch) =>
                        branch?.id === formValues?.coachingCenterBranchId
                    )[0]?.name
                    : '',
                  id: formValues.coachingCenterBranchId
                }
                : null
            }
            handleSelect={(item) => {
              dispatch(
                getChildByInstitute({
                  instituteId: coachingCenterId,
                  branchId: item?.id
                })
              )
              dispatch(
                updateFormValues({
                  key: 'coachingCenterBranchId',
                  value: item?.id
                })
              )
              dispatch(resetCourses())
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            title={courseName}
            isRequired
            dropdownList={courseList.concat(availableCoursesList)}
            error={courseError}
            placeholder={selectCourseName}
            defaultValue={
              isEditMode
                ? {
                  name: formValues?.courseId
                    ? courseList?.filter(
                      (course) => course?.id === formValues?.courseId
                    )[0]?.name
                    : '',
                  id: formValues.courseId
                }
                : null
            }
            handleSelect={(item) => {
              dispatch(
                updateFormValues({
                  key: 'courseId',
                  value: item?.id
                })
              )
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            title={upmyranksMaterial}
            dropdownList={availableCoursesList}
            placeholder={selectUpmyranksMaterial}
            defaultValue={
              isEditMode
                ? {
                  name: formValues?.additionalCourseId
                    ? availableCoursesList?.filter(
                      (course) =>
                        course?.id === formValues?.additionalCourseId
                    )[0]?.name
                    : '',
                  id: formValues.additionalCourseId
                }
                : null
            }
            handleSelect={(item) => {
              dispatch(
                updateFormValues({
                  key: 'additionalCourseId',
                  value: item?.id
                })
              )
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={batchStartDateValue}
            onSelect={(date: Date) => {
              dispatch(
                updateFormValues({
                  key: 'batchStartDate',
                  value: date
                })
              )
            }}
            onChange={(date: Date) => {
              dispatch(
                updateFormValues({
                  key: 'batchStartDate',
                  value: date
                })
              )
            }}
            placeholderText={batchStartDate}
            customInput={
              <Input
                value={batchStartDate}
                inputType="text"
                label={batchStartDate}
                placeholder={batchStartDate}
                suffix={['far', 'calendar']}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={batchEndDateValue}
            onSelect={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'batchEndDate',
                  value: time
                })
              )
            }}
            onChange={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'batchEndDate',
                  value: time
                })
              )
            }}
            placeholderText={batchEndDate}
            customInput={
              <Input
                value={batchEndDate}
                inputType="text"
                label={batchEndDate}
                placeholder={batchEndDate}
                suffix={['far', 'calendar']}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={startTimingValue}
            onSelect={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'startTiming',
                  value: time
                })
              )
            }}
            onChange={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'startTiming',
                  value: time
                })
              )
            }}
            showTimeSelectOnly
            showTimeSelect
            timeIntervals={30}
            timeCaption="Time"
            dateFormat="h:mm aa"
            popperPlacement="bottom"
            placeholderText={startTiming}
            customInput={
              <Input
                value={startTimingValue}
                inputType="text"
                label={startTiming}
                placeholder={startTiming}
                suffix={['far', 'clock']}
                onBlur={() => {
                  handleValidate('startTiming')
                }}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <DatePicker
            selected={endTimingValue}
            onSelect={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'endTiming',
                  value: time
                })
              )
            }}
            onChange={(time: Date) => {
              dispatch(
                updateFormValues({
                  key: 'endTiming',
                  value: time
                })
              )
            }}
            showTimeSelectOnly
            showTimeSelect
            timeIntervals={30}
            timeCaption="Time"
            dateFormat="h:mm aa"
            popperPlacement="bottom"
            placeholderText={endTiming}
            customInput={
              <Input
                value={endTimingValue}
                inputType="text"
                label={endTiming}
                placeholder={endTiming}
                suffix={['far', 'clock']}
                onBlur={() => {
                  handleValidate('endTiming')
                }}
              />
            }
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <Input
            label={batchSize}
            isRequired
            placeholder={batchSize}
            value={batchSizeValue}
            inputType="number"
            name="batchSize"
            error={batchSizeError}
            onBlur={() => {
              handleValidate('batchSize')
            }}
            onChange={(value) => {
              dispatch(
                updateFormValues({
                  key: 'batchSize',
                  value: value
                })
              )
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <Input
            label={description}
            isRequired
            placeholder={description}
            value={descriptionValue}
            error={descriptionError}
            name="description"
            onBlur={() => {
              handleValidate('description')
            }}
            onChange={(value) => {
              dispatch(
                updateFormValues({
                  key: 'description',
                  value: value
                })
              )
            }}
          />
        </DropdownWrapper>
        <DropdownWrapper>
          <EditableDropdown
            title={status}
            isRequired
            dropdownList={statusList}
            error={statusError}
            placeholder={selectAnOption}
            defaultValue={
              isEditMode
                ? {
                  name: formValues?.status,
                  id: formValues.status
                    ? statusList?.filter(
                      (status) => status?.name === formValues?.status
                    )[0]?.id
                    : ''
                }
                : null
            }
            handleSelect={(item) => {
              dispatch(
                updateFormValues({
                  key: 'status',
                  value: item?.id
                })
              )
            }}
          />
        </DropdownWrapper>
      </FlexWrapper>
      {isLoading ? (
        <Loader />
      ) : (
        <FlexWrapper justifyContent="center">
          <ActionButton
            variant="outline-secondary"
            onClick={() => history.goBack()}
          >
            {back}
          </ActionButton>
          <Button onClick={handleSubmit}>{save}</Button>
        </FlexWrapper>
      )}
    </PageWrapper>
  )
}

export default AddBatch
